import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CCol, CFormGroup, CRow } from '@coreui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { TimePicker } from 'antd';
import locale from 'antd/es/date-picker/locale/fr_FR';
import localeEn from 'antd/es/date-picker/locale/en_US';
import i18next from 'i18next';
import moment from 'moment';
import pica from 'pica';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { isTelusOrg, isChardOrg } from '../users/constant';
import styled from 'styled-components';
import * as Yup from 'yup';

import { CustomDatePicker } from '../../components/CustomSearchBar';
import { ModalHeader } from '../../components/Modals';
import { WizardModalLayout } from '../../components/ModalScrollProgress';
import { StyledModalCardV2 } from '../../components/modalV2';
import {
  WizardFooterButtonPrimary,
  WizardFooterButtonSecondary
} from '../../components/WizardV2/wizardStylesV2';
import AmenitiesRichTextEditor from '../../components/AmenitiesRichTextEditor';
import { Fonts, Palette } from '../../components/utils';
import AvatarCropper, { ErrorText } from '../../components/utils/AvatarCropper';
import {
  EntityImagesGallery,
  IMAGE_ENTITY_TYPE,
  buildImageManifest,
  buildImagesBatchFormData,
  buildImageSyncFormData,
  createNextUploadKey,
  createNewImageItem,
  mapEntityImages,
  manifestsEqual,
  revokeImageBlobUrls
} from '../../components/EntityImagesGallery';
import { IconSelector, resolveIconSrc } from '../../components/IconSelector';
import { defaultImgCDN, imageString, ImagesOptions } from '../../helper';
import { changeOpenedModal } from '../../redux/actions/headerModal';
import {
  addEvent,
  deleteEventImage,
  editEvent,
  getEvent,
  getEvents,
  syncEventImages,
  uploadEventImage,
  uploadEventImagesBatch
} from '../../redux/actions/events';
import { setToast } from '../../redux/actions/window';
import { ADD_EVENT, EDIT_EVENT, GET_EVENT } from '../../redux/constants';
import { requestPending, requestSuccess } from '../../utils/status';
import {
  InputSeparator,
  SettingsModalBoldText,
  SettingsModalInput
} from '../settings/SettingsModals';
import { FeatureSwitch } from '../offers/styles';
import { isQuillContentEmpty, MediaKeys, offerImageValidations } from '../offers/utils';
import {
  ToggleBlock,
  ToggleCopy,
  ToggleDescription,
  ToggleRow,
  ToggleSwitchWrap,
  ToggleTitle
} from '../properties/Unit&Amenities/Amenities/V2/addAmenityWizardStylesV2';
import { DATE_FORMATS } from '../users/constant';
import {
  formatEventTimeForApi,
  hasEventStarted,
  isValidEventTimeRange,
  parseEventTime
} from './Events';
import { EVENT_IMAGE_TYPE, EVENT_STATUS, isEventVisible } from './constants';

export const EVENT_MODAL_PROPS = {
  contentPadding: '32px 24px 16px',
  dialogMaxWidth: '548px'
};

const TIME_DISPLAY_FORMAT = DATE_FORMATS.hh_mm_A;
const DATE_FORMAT = DATE_FORMATS.YYYY_MM_DD;
const HTTPS_URL_REGEX =
  /^https:\/\/(?:www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_\+.~#?&\/=]*)$/;

const getDefaultValues = () => ({
  title: '',
  sub_heading: '',
  description: '',
  event_date: null,
  start_time: null,
  end_time: null,
  location: '',
  icon: '',
  tag: '',
  cta_text: '',
  cta_link: '',
  pinned: false,
  status: EVENT_STATUS.VISIBLE
});

const normalizeDescription = (description) =>
  isQuillContentEmpty(description) ? '' : description || '';

const buildPayload = (values, building) => ({
  title: values.title?.trim() || '',
  sub_heading: values.sub_heading?.trim() || '',
  description: normalizeDescription(values.description),
  event_date: moment(values.event_date).format(DATE_FORMAT),
  start_time: formatEventTimeForApi(values.start_time),
  end_time: formatEventTimeForApi(values.end_time),
  location: values.location?.trim() || '',
  icon: values.icon?.trim() || '',
  tag: values.tag?.trim() || '',
  cta_text: values.cta_text?.trim() || '',
  cta_link: values.cta_link?.trim() || '',
  pinned: Boolean(values.pinned),
  status: Number(values.status),
  status_id: Number(values.status),
  building_id: building?.id,
  org_id: building?.org_id
});

const toFormData = (payload, files = {}) => {
  const formData = new FormData();
  Object.entries(payload).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      return;
    }
    formData.append(key, typeof value === 'boolean' ? String(value) : value);
  });

  Object.entries(files).forEach(([key, file]) => {
    if (!file?.blob) {
      return;
    }
    formData.append(key, file.blob, file.fileName || `${key}.png`);
  });

  return formData;
};

const emptyAvatarState = () => ({ image: null, name: null });

const exportCroppedImage = (editorRef, mediaKey, fileName) =>
  new Promise((resolve, reject) => {
    if (!editorRef?.current) {
      reject(new Error('Missing crop editor'));
      return;
    }

    const sourceCanvas = editorRef.current.getImage();
    const dims = ImagesOptions(3)[mediaKey]?.DIMENSION;
    const offScreenCanvas = document.createElement('canvas');
    offScreenCanvas.width = dims?.WIDTH || sourceCanvas.width;
    offScreenCanvas.height = dims?.HEIGHT || sourceCanvas.height;

    pica()
      .resize(sourceCanvas, offScreenCanvas)
      .then((img) => {
        img.toBlob((blob) => {
          if (!blob) {
            reject(new Error('Failed to export image'));
            return;
          }
          resolve({ blob, fileName: fileName || 'image.png' });
        }, 'image/png');
      })
      .catch(reject);
  });

const uploadEventImageFile = (dispatch, eventId, imageType, blob, fileName) =>
  new Promise((resolve, reject) => {
    const body = new FormData();
    body.append('image', blob, fileName);
    dispatch(
      uploadEventImage({
        id: eventId,
        imageType,
        body,
        success: resolve,
        fail: reject
      })
    );
  });

export const getEventImageUrl = (event, imageType) => {
  if (!event) {
    return '';
  }
  const raw =
    imageType === EVENT_IMAGE_TYPE.BANNER
      ? event.banner || event.banner_image || event.event_banner || ''
      : event.thumbnail || event.thumbnail_image || event.event_thumbnail || '';
  return raw ? imageString(raw) : '';
};

const buildDirtyPayload = (values, dirtyFields, building) => {
  const fullPayload = buildPayload(values, building);
  const body = {};

  Object.keys(dirtyFields || {}).forEach((key) => {
    if (key === 'building_id' || key === 'org_id') {
      return;
    }
    if (Object.prototype.hasOwnProperty.call(fullPayload, key)) {
      body[key] = fullPayload[key];
    }
  });
  if ('status' in body) {
    body.status_id = body.status;
  }
  if (('status' in body || 'pinned' in body) && building?.id) {
    const setting = { building_id: building.id };
    if ('status' in body) {
      setting.visible = body.status === EVENT_STATUS.VISIBLE;
    }
    if ('pinned' in body) {
      setting.pinned = body.pinned;
    }
    body.buildings = [setting];
  }

  return body;
};

const matchIconValue = (rawIcon, amenityTypes) => {
  const raw = typeof rawIcon === 'string' ? rawIcon.trim() : '';
  if (!raw) {
    return '';
  }
  const resolved = resolveIconSrc(raw);
  const match = (amenityTypes || []).find((a) => {
    const icon = typeof a.icon === 'string' ? a.icon.trim() : '';
    if (!icon) {
      return false;
    }
    return icon === raw || resolveIconSrc(icon) === resolved;
  });
  return match ? String(match.icon).trim() : raw;
};

const mapEventToFormValues = (event, amenityTypes) => ({
  title: event?.title || '',
  sub_heading: event?.sub_heading || '',
  description: event?.description || '',
  event_date: event?.event_date ? moment(event.event_date) : null,
  start_time: parseEventTime(event?.start_time),
  end_time: parseEventTime(event?.end_time),
  location: event?.location || '',
  icon: matchIconValue(event?.icon, amenityTypes),
  tag: event?.tag || '',
  cta_text: event?.cta_text || '',
  cta_link: event?.cta_link || '',
  pinned: Boolean(event?.pinned),
  status: event && !isEventVisible(event) ? EVENT_STATUS.HIDDEN : EVENT_STATUS.VISIBLE
});

const AddEditEvent = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bannerAvatar, setBannerAvatar] = useState(emptyAvatarState);
  const [thumbnailAvatar, setThumbnailAvatar] = useState(emptyAvatarState);
  const [isBannerDeleted, setIsBannerDeleted] = useState(false);
  const [isThumbnailDeleted, setIsThumbnailDeleted] = useState(false);
  const [additionalImages, setAdditionalImages] = useState([]);
  const bannerAvatarRef = useRef(null);
  const thumbnailAvatarRef = useRef(null);
  const initialAdditionalImagesManifestRef = useRef(buildImageManifest([]));
  const nextUploadKeyRef = useRef(0);
  const isSubmittingRef = useRef(false);

  const modal = useSelector((state) => state.headerModal.modal);
  const eventData = useSelector((state) => state.headerModal.data);
  const event = useSelector((state) => state.events.event);
  const getEventStatus = useSelector((state) => state.events.getEventStatus);
  const globalSelectedBuilding = useSelector(
    (state) => state.building.globalSelectedBuilding
  );
  const globalOrgId = useSelector((state) => state.Organization.globalOrgId);
  const lastListParams = useSelector((state) => state.events.lastListParams);
  const amenityTypes = useSelector((state) => state.amenities.amenityTypes);
  const iconOptions = useMemo(
    () => {
      const telusRegex = /\/[^/]*telus[^/]*$/i;
      const chardRegex = /\/[^/]*chard[^/]*$/i;
      const orgId = globalSelectedBuilding?.org_id || globalOrgId;
      const isTelus = isTelusOrg(orgId);
      const isChard = isChardOrg(orgId);

      return (amenityTypes || [])
        .map((a) => {
          const icon = typeof a.icon === 'string' ? a.icon.trim() : '';
          return { value: icon, label: a.Description, icon };
        })
        .filter((o) => {
          if (!o.value) return false;
          if (isTelus) return telusRegex.test(o.icon);
          if (isChard) return chardRegex.test(o.icon);
          return true;
        });
    },
    [amenityTypes, globalSelectedBuilding, globalOrgId]
  );
  const addEventStatus = useSelector((state) => state.events.addEventStatus);
  const editEventStatus = useSelector((state) => state.events.editEventStatus);

  const show = modal === 'addEditEvent';
  const isEdit = show && Boolean(eventData?.id);
  const building = {
    id: globalSelectedBuilding?.id,
    org_id: globalSelectedBuilding?.org_id || globalOrgId,
    photo: globalSelectedBuilding?.photo,
    name: globalSelectedBuilding?.name
  };
  const hasPendingImages = Boolean(bannerAvatar.image || thumbnailAvatar.image);
  const isAdditionalImagesDirty = !manifestsEqual(
    initialAdditionalImagesManifestRef.current,
    buildImageManifest(additionalImages)
  );
  const hasPendingAdditionalImages = additionalImages.some(
    (img) => img?.file instanceof File
  );
  const hasPendingImageChanges =
    hasPendingImages ||
    isBannerDeleted ||
    isThumbnailDeleted ||
    isAdditionalImagesDirty;

  const validationSchema = Yup.object().shape({
    title: Yup.string()
      .trim()
      .max(100, t('Not more than 100 characters'))
      .required(t('Title is required')),
    sub_heading: Yup.string().trim().nullable(),
    description: Yup.string()
      .nullable()
      .max(50000, t('Not more than 50000 characters')),
    event_date: Yup.mixed()
      .required(t('Event date is required'))
      .test('is-valid-date', t('Event date is required'), (value) =>
        moment(value).isValid()
      )
      .test(
        'not-before-today',
        t('Cannot select a date before today'),
        (value) => !value || !moment(value).isBefore(moment().startOf('day'))
      ),
    start_time: Yup.mixed()
      .required(t('Start time is required'))
      .test('is-valid-start', t('Start time is required'), (value) =>
        Boolean(parseEventTime(value))
      )
      .test('not-in-past', t('Start time cannot be in the past'), function(value) {
        const { event_date } = this.parent;
        const buildingState = globalSelectedBuilding?.state;
        
        return !hasEventStarted(event_date, value, buildingState);
      }),
    end_time: Yup.mixed()
      .required(t('End time is required'))
      .test('is-valid-end', t('End time is required'), (value) =>
        Boolean(parseEventTime(value))
      )
      .test('after-start', t('End time must be after start time'), function (value) {
        return isValidEventTimeRange(this.parent.start_time, value);
      }),
    location: Yup.string().trim().nullable(),
    icon: Yup.string().trim().nullable(),
    tag: Yup.string().trim().nullable(),
    cta_text: Yup.string()
      .trim()
      .max(50, t('Not more than 50 characters'))
      .nullable()
      .test('cta-dependency', t('CTA Text is required with CTA Link'), function(value) {
        const { cta_link } = this.parent;
        const hasText = Boolean(value?.trim());
        const hasLink = Boolean(cta_link?.trim());
        
        if (!hasText && hasLink) {
          return false;
        }
        
        return true;
      }),
    cta_link: Yup.string()
      .trim()
      .nullable()
      .test('https-url', t('HTTPS required'), (value) => {
        if (!value) {
          return true;
        }
        return HTTPS_URL_REGEX.test(value);
      })
      .test('cta-dependency', t('CTA Link is required with CTA Text'), function(value) {
        const { cta_text } = this.parent;
        const hasLink = Boolean(value?.trim());
        const hasText = Boolean(cta_text?.trim());
        
        if (!hasLink && hasText) {
          return false;
        }
        
        return true;
      }),
    pinned: Yup.boolean().default(false),
    status: Yup.number()
      .oneOf([EVENT_STATUS.VISIBLE, EVENT_STATUS.HIDDEN])
      .default(EVENT_STATUS.VISIBLE)
  });

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    getValues,
    trigger,
    formState: { errors, isDirty, dirtyFields }
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(validationSchema),
    defaultValues: getDefaultValues()
  });

  useEffect(() => {
    if (!show) {
      return;
    }

    setBannerAvatar(emptyAvatarState());
    setThumbnailAvatar(emptyAvatarState());
    setIsBannerDeleted(false);
    setIsThumbnailDeleted(false);
    setAdditionalImages([]);
    initialAdditionalImagesManifestRef.current = buildImageManifest([]);
    nextUploadKeyRef.current = 0;

    if (!isEdit) {
      reset(getDefaultValues());
      return;
    }

    if (!eventData?.id) {
      return;
    }

    reset(getDefaultValues());
    dispatch(
      getEvent({
        id: eventData.id,
        fail: () => {
          dispatch(
            setToast({
              toastShow: true,
              toastMessage: t('Failed to load event!'),
              modal: 'errorToast'
            })
          );
        }
      })
    );
  }, [show, isEdit, eventData?.id, dispatch, reset, t]);

  useEffect(() => {
    if (
      !isEdit ||
      !eventData?.id ||
      getEventStatus !== requestSuccess(GET_EVENT) ||
      !event ||
      Number(event.id) !== Number(eventData.id)
    ) {
      return;
    }

    reset(mapEventToFormValues(event, amenityTypes));
    const mappedImages = mapEntityImages(
      event.additional_images || event.images || []
    );
    setAdditionalImages(mappedImages);
    initialAdditionalImagesManifestRef.current = buildImageManifest(mappedImages);
    nextUploadKeyRef.current = 0;
  }, [isEdit, eventData?.id, getEventStatus, event, reset]);

  useEffect(() => {
    if (
      !isEdit ||
      !eventData?.id ||
      !event ||
      Number(event.id) !== Number(eventData.id)
    ) {
      return;
    }
    const matched = matchIconValue(event.icon, amenityTypes);
    const current = getValues('icon');
    const currentResolved = resolveIconSrc(current);
    const eventResolved = resolveIconSrc(event.icon);
    if (!current || currentResolved === eventResolved) {
      setValue('icon', matched, { shouldDirty: false, shouldValidate: false });
    }
  }, [amenityTypes, isEdit, event, eventData?.id, getValues, setValue]);

  const mapNewAdditionalImage = (file) =>
    createNewImageItem(file, createNextUploadKey(nextUploadKeyRef));

  const disabledDate = (current) => current && current < moment().startOf('day');

  const resetImages = () => {
    setBannerAvatar(emptyAvatarState());
    setThumbnailAvatar(emptyAvatarState());
    setIsBannerDeleted(false);
    setIsThumbnailDeleted(false);
    revokeImageBlobUrls(additionalImages);
    setAdditionalImages([]);
    initialAdditionalImagesManifestRef.current = buildImageManifest([]);
    nextUploadKeyRef.current = 0;
  };

  const closeModal = () => {
    reset(getDefaultValues());
    resetImages();
    dispatch(changeOpenedModal({ modal: '' }));
  };

  const onClose = () => {
    if (isSubmittingRef.current) return;
    closeModal();
  };

  const refetchEvents = () => {
    if (lastListParams) {
      dispatch(getEvents({ params: lastListParams }));
      return;
    }
    if (!building.id || !building.org_id) {
      return;
    }
    dispatch(
      getEvents({
        params: {
          building: building.id,
          org: building.org_id,
          page: 1
        }
      })
    );
  };

  const onSuccess = (savedEvent) => {
    const isVisible = savedEvent ? isEventVisible(savedEvent) : isEdit && isEventVisible(event);
    const notifyId = savedEvent?.id ?? (isEdit ? eventData.id : null);
    isSubmittingRef.current = false;
    setIsSubmitting(false);
    resetImages();
    dispatch(
      setToast({
        toastShow: true,
        toastMessage: isEdit
          ? t('Event updated successfully.')
          : t('Event created successfully.')
      })
    );
    refetchEvents();
    closeModal();
    if (notifyId && isVisible) {
      const notification = savedEvent ? savedEvent.notification : event?.notification;
      dispatch(changeOpenedModal({ modal: 'notifyEvent', data: { id: notifyId, notification } }));
    }
  };

  const onFail = (message) => {
    isSubmittingRef.current = false;
    setIsSubmitting(false);
    dispatch(
      setToast({
        toastShow: true,
        toastMessage:
          message ||
          (isEdit ? t('Failed to update event!') : t('Failed to create event!')),
        modal: 'errorToast'
      })
    );
  };

  const collectPendingImageFiles = async () => {
    const files = {};

    if (bannerAvatar.image && bannerAvatarRef.current) {
      files[EVENT_IMAGE_TYPE.BANNER] = await exportCroppedImage(
        bannerAvatarRef,
        MediaKeys.EVENT_BANNER,
        bannerAvatar.name || 'banner.png'
      );
    }

    if (thumbnailAvatar.image && thumbnailAvatarRef.current) {
      files[EVENT_IMAGE_TYPE.THUMBNAIL] = await exportCroppedImage(
        thumbnailAvatarRef,
        MediaKeys.EVENT_THUMBNAIL,
        thumbnailAvatar.name || 'thumbnail.png'
      );
    }

    return files;
  };

  const onSubmit = async (values) => {
    if (!building.id || !building.org_id) {
      dispatch(
        setToast({
          toastShow: true,
          toastMessage: t('Please select a building!'),
          modal: 'errorToast'
        })
      );
      return;
    }

    if (isEdit && hasEventStarted(event?.event_date, event?.start_time, globalSelectedBuilding?.state)) {
      dispatch(
        setToast({
          toastShow: true,
          toastMessage: t('This event has already started and cannot be updated!'),
          modal: 'errorToast'
        })
      );
      return;
    }

    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setIsSubmitting(true);

    try {
      if (isEdit) {
        const body = buildDirtyPayload(values, dirtyFields, building);
        const eventId = eventData.id;
        const hasMetadataChanges = Object.keys(body).length > 0;
        const shouldDeleteBanner = isBannerDeleted && !bannerAvatar.image;
        const shouldUploadBanner = Boolean(bannerAvatar.image && bannerAvatarRef.current);
        const shouldDeleteThumbnail = isThumbnailDeleted && !thumbnailAvatar.image;
        const shouldUploadThumbnail = Boolean(
          thumbnailAvatar.image && thumbnailAvatarRef.current
        );
        const hasImageChanges =
          shouldDeleteBanner ||
          shouldUploadBanner ||
          shouldDeleteThumbnail ||
          shouldUploadThumbnail;
        const needsAdditionalImagesSync = isAdditionalImagesDirty;

        if (!hasMetadataChanges && !hasImageChanges && !needsAdditionalImagesSync) {
          isSubmittingRef.current = false;
          setIsSubmitting(false);
          return;
        }

        let savedEvent;
        const metadataTask = async () => {
          if (!hasMetadataChanges) return;
          const res = await new Promise((resolve, reject) => {
            dispatch(
              editEvent({
                id: eventId,
                body,
                success: resolve,
                fail: reject
              })
            );
          });
          savedEvent = res?.data;
        };

        const imageSequenceTask = async () => {
          if (shouldDeleteBanner) {
            await new Promise((resolve, reject) => {
              dispatch(
                deleteEventImage({
                  id: eventId,
                  imageType: EVENT_IMAGE_TYPE.BANNER,
                  success: resolve,
                  fail: reject
                })
              );
            });
          } else if (shouldUploadBanner) {
            const file = await exportCroppedImage(
              bannerAvatarRef,
              MediaKeys.EVENT_BANNER,
              bannerAvatar.name || 'banner.png'
            );
            await uploadEventImageFile(
              dispatch,
              eventId,
              EVENT_IMAGE_TYPE.BANNER,
              file.blob,
              file.fileName
            );
          }

          if (shouldDeleteThumbnail) {
            await new Promise((resolve, reject) => {
              dispatch(
                deleteEventImage({
                  id: eventId,
                  imageType: EVENT_IMAGE_TYPE.THUMBNAIL,
                  success: resolve,
                  fail: reject
                })
              );
            });
          } else if (shouldUploadThumbnail) {
            const file = await exportCroppedImage(
              thumbnailAvatarRef,
              MediaKeys.EVENT_THUMBNAIL,
              thumbnailAvatar.name || 'thumbnail.png'
            );
            await uploadEventImageFile(
              dispatch,
              eventId,
              EVENT_IMAGE_TYPE.THUMBNAIL,
              file.blob,
              file.fileName
            );
          }
        };

        const additionalImagesTask = async () => {
          if (!needsAdditionalImagesSync) return;
          await new Promise((resolve, reject) => {
            dispatch(
              syncEventImages({
                body: buildImageSyncFormData({
                  images: additionalImages,
                  entityId: eventId,
                  entityType: IMAGE_ENTITY_TYPE.EVENT,
                  orgId: building.org_id,
                  buildingId: building.id
                }),
                success: resolve,
                fail: reject
              })
            );
          });
        };

        await Promise.all([metadataTask(), imageSequenceTask(), additionalImagesTask()]);
        onSuccess(savedEvent);
        return;
      }

      const payload = buildPayload(values, building);
      const files = hasPendingImages ? await collectPendingImageFiles() : {};
      const createResponse = await new Promise((resolve, reject) => {
        dispatch(
          addEvent({
            body: toFormData(payload, files),
            success: resolve,
            fail: reject
          })
        );
      });
      const createdEventId =
        createResponse?.data?.id ??
        createResponse?.data?.data?.id ??
        createResponse?.data?.new_id;

      if (hasPendingAdditionalImages) {
        if (!createdEventId) {
          throw new Error('missing-event-id');
        }
        await new Promise((resolve, reject) => {
          dispatch(
            uploadEventImagesBatch({
              body: buildImagesBatchFormData({
                images: additionalImages,
                entityId: createdEventId,
                entityType: IMAGE_ENTITY_TYPE.EVENT,
                orgId: building.org_id,
                buildingId: building.id
              }),
              success: resolve,
              fail: reject
            })
          );
        });
      }
      onSuccess(createResponse?.data);
    } catch (error) {
      onFail(
        isEdit
          ? t('Failed to update event!')
          : error?.message === 'missing-event-id'
            ? t('Event created, but additional images could not be uploaded!')
            : undefined
      );
    }
  };

  const pending =
    isSubmitting ||
    getEventStatus === requestPending(GET_EVENT) ||
    addEventStatus === requestPending(ADD_EVENT) ||
    editEventStatus === requestPending(EDIT_EVENT);

  const isEventLoaded =
    isEdit &&
    getEventStatus === requestSuccess(GET_EVENT) &&
    event &&
    Number(event.id) === Number(eventData?.id);

  const existingBannerUrl = isEventLoaded
    ? getEventImageUrl(event, EVENT_IMAGE_TYPE.BANNER)
    : '';
  const existingThumbnailUrl = isEventLoaded
    ? getEventImageUrl(event, EVENT_IMAGE_TYPE.THUMBNAIL)
    : '';

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <WizardModalLayout
        Modal={StyledModalCardV2}
        modalProps={EVENT_MODAL_PROPS}
        show={show}
        onClose={onClose}
        width="548px"
        bodyGap="24px"
        closeOnBackdrop={false}
        deps={[show, isEdit]}
        footerActions={
          <>
            <WizardFooterButtonSecondary type="button" onClick={onClose} disabled={isSubmitting}>
              {t('Cancel')}
            </WizardFooterButtonSecondary>
            <WizardFooterButtonPrimary
              type="submit"
              disabled={pending || (isEdit && !isDirty && !hasPendingImageChanges)}>
              {isSubmitting
                ? isEdit
                  ? t('Saving...')
                  : t('Creating...')
                : isEdit
                  ? t('Save Changes')
                  : t('Create')}
            </WizardFooterButtonPrimary>
          </>
        }>
        <StickyEventModalHeader $closeDisabled={isSubmitting}>
          <ModalHeader
            title={isEdit ? t('Edit Event') : t('Create New Event')}
            v2
            onCloseCallback={onClose}
          />
        </StickyEventModalHeader>

        <EventFormBody $locked={isSubmitting} aria-busy={isSubmitting} inert={isSubmitting ? '' : undefined}>
        <CRow>
          <CCol>
            <InputSeparator>
              <SettingsModalBoldText htmlFor="title">{t('Title')}</SettingsModalBoldText>
              <Controller
                control={control}
                name="title"
                render={({ field: { onChange, onBlur, value } }) => (
                  <SettingsModalInput
                    id="title"
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    placeholder={t('Title...')}
                    error={!!errors.title}
                  />
                )}
              />
              {errors.title && <ErrorText>{errors.title.message}</ErrorText>}
            </InputSeparator>
          </CCol>
        </CRow>

        <CRow>
          <CCol>
            <InputSeparator>
              <SettingsModalBoldText htmlFor="sub_heading">
                {t('Subheading')}
              </SettingsModalBoldText>
              <Controller
                control={control}
                name="sub_heading"
                render={({ field: { onChange, onBlur, value } }) => (
                  <SettingsModalInput
                    id="sub_heading"
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    placeholder={t('Subheading...')}
                    error={!!errors.sub_heading}
                  />
                )}
              />
              {errors.sub_heading && <ErrorText>{errors.sub_heading.message}</ErrorText>}
            </InputSeparator>
          </CCol>
        </CRow>

        <CRow>
          <CCol>
            <CFormGroup>
              <SettingsModalBoldText>{t('Event Banner')}</SettingsModalBoldText>
              <AvatarCropper
                name="event-banner"
                avatar={existingBannerUrl || defaultImgCDN}
                newAvatarState={{
                  newAvatar: bannerAvatar,
                  setNewAvatar: setBannerAvatar
                }}
                editorRef={bannerAvatarRef}
                uploaderLength={220}
                withPreview
                previewLength={110}
                isEdit={isEdit}
                allowResetDefault={isEdit && Boolean(existingBannerUrl)}
                resetState={{
                  isResetted: isBannerDeleted,
                  setIsResetted: setIsBannerDeleted
                }}
                resetDefaultText={t('Delete Banner')}
                resetOriginalText={t('Restore Banner')}
                validationSet={offerImageValidations}
                mediaKey={MediaKeys.EVENT_BANNER}
                subtitle={t('428x220 image for event banner')}
              />
            </CFormGroup>
          </CCol>
        </CRow>

        <CRow>
          <CCol>
            <CFormGroup>
              <SettingsModalBoldText>{t('Event Thumbnail')}</SettingsModalBoldText>
              <AvatarCropper
                name="event-thumbnail"
                avatar={existingThumbnailUrl || defaultImgCDN}
                newAvatarState={{
                  newAvatar: thumbnailAvatar,
                  setNewAvatar: setThumbnailAvatar
                }}
                editorRef={thumbnailAvatarRef}
                uploaderLength={160}
                withPreview
                previewLength={100}
                isEdit={isEdit}
                allowResetDefault={isEdit && Boolean(existingThumbnailUrl)}
                resetState={{
                  isResetted: isThumbnailDeleted,
                  setIsResetted: setIsThumbnailDeleted
                }}
                resetDefaultText={t('Delete Thumbnail')}
                resetOriginalText={t('Restore Thumbnail')}
                validationSet={offerImageValidations}
                mediaKey={MediaKeys.EVENT_THUMBNAIL}
                subtitle={t('160x160 image for event thumbnail')}
              />
            </CFormGroup>
          </CCol>
        </CRow>

        <CRow>
          <CCol>
            <InputSeparator>
              <SettingsModalBoldText htmlFor="description">
                {t('Description')}
              </SettingsModalBoldText>
              <Controller
                control={control}
                name="description"
                render={({ field: { onChange, value } }) => (
                  <AmenitiesRichTextEditor
                    value={value || ''}
                    onChange={onChange}
                    placeholder={t('Description...')}
                    setValue={setValue}
                    fieldName="description"
                  />
                )}
              />
              {errors.description && <ErrorText>{errors.description.message}</ErrorText>}
            </InputSeparator>
          </CCol>
        </CRow>

        <CRow>
          <CCol>
            <InputSeparator>
              <SettingsModalBoldText htmlFor="event_date">
                {t('Event Date')}
              </SettingsModalBoldText>
              <Controller
                control={control}
                name="event_date"
                render={({ field: { onChange, onBlur, value } }) => (
                  <CustomDatePicker
                    height="56px"
                    locale={i18next.resolvedLanguage === 'fr' ? locale : localeEn}
                    onChange={onChange}
                    value={value}
                    onBlur={onBlur}
                    id="event_date"
                    placeholder={t('Select Date...')}
                    error={!!errors.event_date}
                    format={DATE_FORMAT}
                    disabledDate={disabledDate}
                    style={{ width: '100%' }}
                    className={!!errors.event_date && 'input-error'}
                  />
                )}
              />
              {errors.event_date && <ErrorText>{errors.event_date.message}</ErrorText>}
            </InputSeparator>
          </CCol>
        </CRow>

        <CRow>
          <CCol md="6">
            <InputSeparator>
              <SettingsModalBoldText htmlFor="start_time">
                {t('Start Time')}
              </SettingsModalBoldText>
              <Controller
                control={control}
                name="start_time"
                render={({ field: { onChange, value } }) => (
                  <StyledTimePicker
                    id="start_time"
                    value={parseEventTime(value)}
                    onChange={(next) => {
                      onChange(next);
                      if (getValues('end_time')) {
                        trigger('end_time');
                      }
                    }}
                    format={TIME_DISPLAY_FORMAT}
                    use12Hours
                    showNow={false}
                    placeholder={t('Select Start Time')}
                    style={{ width: '100%' }}
                    className={!!errors.start_time && 'input-error'}
                  />
                )}
              />
              {errors.start_time && <ErrorText>{errors.start_time.message}</ErrorText>}
            </InputSeparator>
          </CCol>
          <CCol md="6">
            <InputSeparator>
              <SettingsModalBoldText htmlFor="end_time">{t('End Time')}</SettingsModalBoldText>
              <Controller
                control={control}
                name="end_time"
                render={({ field: { onChange, value } }) => (
                  <StyledTimePicker
                    id="end_time"
                    value={parseEventTime(value)}
                    onChange={onChange}
                    format={TIME_DISPLAY_FORMAT}
                    use12Hours
                    showNow={false}
                    placeholder={t('Select End Time')}
                    style={{ width: '100%' }}
                    className={!!errors.end_time && 'input-error'}
                  />
                )}
              />
              {errors.end_time && <ErrorText>{errors.end_time.message}</ErrorText>}
            </InputSeparator>
          </CCol>
        </CRow>

        <CRow>
          <CCol>
            <InputSeparator>
              <SettingsModalBoldText htmlFor="location">{t('Location')}</SettingsModalBoldText>
              <Controller
                control={control}
                name="location"
                render={({ field: { onChange, onBlur, value } }) => (
                  <SettingsModalInput
                    id="location"
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    placeholder={t('Location...')}
                    error={!!errors.location}
                  />
                )}
              />
              {errors.location && <ErrorText>{errors.location.message}</ErrorText>}
            </InputSeparator>
          </CCol>
        </CRow>

        <CRow>
          <CCol>
            <InputSeparator>
              <SettingsModalBoldText htmlFor="icon">{t('Location Icon')}</SettingsModalBoldText>
              <Controller
                control={control}
                name="icon"
                render={({ field: { onChange, onBlur, value, ref } }) => (
                  <IconSelector
                    ref={ref}
                    id="icon"
                    name="icon"
                    options={iconOptions}
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    placeholder={t('Please select')}
                    hasError={!!errors.icon}
                  />
                )}
              />
              {errors.icon && <ErrorText>{errors.icon.message}</ErrorText>}
            </InputSeparator>
          </CCol>
        </CRow>

        <CRow>
          <CCol>
            <InputSeparator>
              <SettingsModalBoldText htmlFor="tag">{t('Tag')}</SettingsModalBoldText>
              <Controller
                control={control}
                name="tag"
                render={({ field: { onChange, onBlur, value } }) => (
                  <SettingsModalInput
                    id="tag"
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    placeholder={t('Tag...')}
                    error={!!errors.tag}
                  />
                )}
              />
              {errors.tag && <ErrorText>{errors.tag.message}</ErrorText>}
            </InputSeparator>
          </CCol>
        </CRow>

        <CRow>
          <CCol md="6">
            <InputSeparator>
              <SettingsModalBoldText htmlFor="cta_text">{t('CTA Text')}</SettingsModalBoldText>
              <Controller
                control={control}
                name="cta_text"
                render={({ field: { onChange, onBlur, value } }) => (
                  <SettingsModalInput
                    id="cta_text"
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    placeholder={t('CTA Text...')}
                    error={!!errors.cta_text}
                  />
                )}
              />
              {errors.cta_text && <ErrorText>{errors.cta_text.message}</ErrorText>}
            </InputSeparator>
          </CCol>
          <CCol md="6">
            <InputSeparator>
              <SettingsModalBoldText htmlFor="cta_link">{t('CTA Link')}</SettingsModalBoldText>
              <Controller
                control={control}
                name="cta_link"
                render={({ field: { onChange, onBlur, value } }) => (
                  <SettingsModalInput
                    id="cta_link"
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    placeholder={t('CTA Link...')}
                    error={!!errors.cta_link}
                  />
                )}
              />
              {errors.cta_link && <ErrorText>{errors.cta_link.message}</ErrorText>}
            </InputSeparator>
          </CCol>
        </CRow>

        <ToggleBlock style={{ marginTop: 8, gap: 16 }}>
          <ToggleRow>
            <ToggleCopy>
              <ToggleTitle>{t('Visible')}</ToggleTitle>
              <ToggleDescription>
                {t('Controls whether residents can see this event.')}
              </ToggleDescription>
            </ToggleCopy>
            <Controller
              control={control}
              name="status"
              render={({ field: { onChange, value } }) => (
                <ToggleSwitchWrap>
                  <FeatureSwitch
                    id="status"
                    shape="pill"
                    color="primary"
                    checked={Number(value) === EVENT_STATUS.VISIBLE}
                    onChange={(e) =>
                      onChange(
                        e.target.checked ? EVENT_STATUS.VISIBLE : EVENT_STATUS.HIDDEN
                      )
                    }
                  />
                </ToggleSwitchWrap>
              )}
            />
          </ToggleRow>

          <ToggleRow>
            <ToggleCopy>
              <ToggleTitle>{t('Pinned')}</ToggleTitle>
              <ToggleDescription>
                {t('Pins this event at the top of the list for residents.')}
              </ToggleDescription>
            </ToggleCopy>
            <Controller
              control={control}
              name="pinned"
              render={({ field: { onChange, value } }) => (
                <ToggleSwitchWrap>
                  <FeatureSwitch
                    id="pinned"
                    shape="pill"
                    color="primary"
                    checked={Boolean(value)}
                    onChange={(e) => onChange(e.target.checked)}
                  />
                </ToggleSwitchWrap>
              )}
            />
          </ToggleRow>
        </ToggleBlock>

        <CRow>
          <CCol>
            <InputSeparator>
              <div>
                <SettingsModalBoldText>{t('Additional Images')}</SettingsModalBoldText>
                <ToggleDescription>
                  {t('Add up to 10 additional images per event.')}
                </ToggleDescription>
              </div>
              <EntityImagesGallery
                t={t}
                images={additionalImages}
                setImages={setAdditionalImages}
                building={globalSelectedBuilding}
                mapNewImage={mapNewAdditionalImage}
                dropzoneTitle={t('Drag and drop your additional event images')}
                dropHereText={t('Drop images here…')}
                constraintsText={t('5MB max file size. JPG and PNG supported.')}
                maxReachedText={t('Max ten images reached.')}
                coverLabel={t('Cover')}
              />
            </InputSeparator>
          </CCol>
        </CRow>
        </EventFormBody>
      </WizardModalLayout>
    </form>
  );
};

const EventFormBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  pointer-events: ${({ $locked }) => ($locked ? 'none' : 'auto')};
  opacity: ${({ $locked }) => ($locked ? 0.72 : 1)};
`;

export const StickyEventModalHeader = styled.div`
  position: sticky;
  top: 0;
  z-index: 3;
  flex-shrink: 0;
  background: ${Palette.WHITE};

  .modal-header {
    margin: 0;
    padding: 0 0 8px;
    border-bottom: none;
    background: ${Palette.WHITE};
  }

  ${({ $closeDisabled }) =>
    $closeDisabled &&
    `
    button {
      pointer-events: none;
      opacity: 0.4;
      cursor: not-allowed;
    }
  `}
`;

const StyledTimePicker = styled(TimePicker)`
  height: 56px;
  border-radius: 10px;
  font-family: ${Fonts.OPEN_SANS_FONT};
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 24px;
  color: ${Palette.BLACK_50};
  background-color: ${Palette.SOLITUDE};
  border: none;
  padding: 0px 20px;
  box-shadow: none;

  .ant-picker-input {
    input {
      color: ${Palette.BLACK_50};
      border: none;
      font-family: ${Fonts.OPEN_SANS_FONT};
      font-size: 16px;
      font-style: normal;
      font-weight: 600;
      line-height: 24px;

      &::placeholder {
        color: ${Palette.BLACK_50};
        font-family: ${Fonts.OPEN_SANS_FONT};
        font-size: 16px;
        font-style: normal;
        font-weight: 600;
        line-height: 24px;
      }
      :hover {
        cursor: pointer;
      }
    }
  }

  :hover {
    border-color: inherit;
    cursor: pointer;
  }

  &.input-error {
    border: 1px solid ${Palette.ERROR || '#e55353'};
  }
`;

export default AddEditEvent;
